import { Html5Qrcode } from 'html5-qrcode';
import { useEffect, useRef, useState } from 'react';
import { BaseDialog } from './BaseDialog';
import { useTranslation } from '../../store';

export const QRScanner = ({ isOpen, onClose }) => {
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const scannerRef = useRef(null);
  const isMountedRef = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      // Clean up when dialog closes
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {}).finally(() => {
          if (scannerRef.current && isMountedRef.current) {
            scannerRef.current.clear();
            scannerRef.current = null;
          }
        });
      }
      return;
    }

    // Clear any existing content
    const readerElement = document.getElementById('reader');
    if (readerElement) {
      readerElement.innerHTML = '';
    }

    const html5QrCode = new Html5Qrcode("reader");
    scannerRef.current = html5QrCode;

    const startScanner = async () => {
      try {
        await html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0
          },
          (decodedText, result) => {
            if (!isMountedRef.current) return;
            console.log('QR Code scanned:', decodedText);
            // Stop scanner before redirecting
            html5QrCode.stop().then(() => {
              html5QrCode.clear();
              scannerRef.current = null;
              window.open(decodedText, '_self');
            }).catch(console.error);
          },
          (errorMessage) => {
            if (!isMountedRef.current) return;
            // Ignore normal scanning errors
            const ignorableErrors = [
              'NotFoundException',
              'No MultiFormat Readers',
              'QR code parse error'
            ];
            if (!ignorableErrors.some(err => errorMessage.includes(err))) {
              console.debug('Scanner error:', errorMessage);
            }
          }
        );
      } catch (err) {
        if (!isMountedRef.current) return;
        setError('Failed to start camera: ' + err.message);
      }
    };

    startScanner();

    return () => {
      // Cleanup when component unmounts or isOpen changes
      if (scannerRef.current === html5QrCode) {
        scannerRef.current.stop().catch(() => {}).finally(() => {
          if (scannerRef.current === html5QrCode && isMountedRef.current) {
            scannerRef.current.clear();
            scannerRef.current = null;
          }
        });
      }
    };
  }, [isOpen, attempt]);

  const handleRetry = () => {
    setError('');
    // Bumping the attempt re-runs the scanner effect, which restarts the camera
    setAttempt((current) => current + 1);
  };

  const handleClose = () => {
    // Ensure scanner is stopped before closing
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {}).finally(() => {
        if (scannerRef.current) {
          scannerRef.current.clear();
          scannerRef.current = null;
        }
        onClose();
      });
    } else {
      onClose();
    }
  };

  return (
    <BaseDialog isOpen={isOpen} onClose={handleClose} title={t('qr')} size="md" showClose={true} t={t}>
      <div className="scanner">
        <div id="reader" style={{ width: '100%', maxWidth: '500px' }}></div>
        {error && (
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <p style={{ color: 'red' }}>{error}</p>
            <button onClick={handleRetry}>{t('retry')}</button>
          </div>
        )}
      </div>
    </BaseDialog>
  );
};